import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  TextField,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Card,
  CardContent,
  Stack,
  InputAdornment,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Divider,
  Avatar,
  Alert,
  CircularProgress,
  TablePagination,
} from '@mui/material';
import {
  Search,
  Visibility,
  CheckCircle,
  Cancel,
  Schedule,
  Assignment,
  MonetizationOn,
  SwapHoriz,
  Undo,
  CancelPresentation,
  Info,
  AttachMoney,
} from '@mui/icons-material';
import { supabase } from '../../supabaseClient';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../components/AuthProvider';
import { useDarkMode, getClozedStyles } from '../../hooks/useDarkMode';

interface RefundRequest {
  id: string;
  order_item_id: string;
  type: 'cancel' | 'exchange' | 'refund';
  reason: string;
  detail: string;
  status: 'pending' | 'approved' | 'rejected' | 'processing' | 'completed';
  admin_note?: string;
  processed_at?: string;
  created_at: string;
  updated_at: string;
  user_id: string;
  image_url?: string | string[];
  // 조인된 데이터
  order_items?: {
    id: string;
    product_name: string;
    total_price: number;
    quantity: number;
    color_name?: string;
    size?: string;
    orders: {
      id: string;
      shipping_address_id: string | null;
      guest_recipient_name?: string | null;
      guest_phone?: string | null;
      guest_address?: string | null;
      guest_detail_address?: string | null;
      guest_postal_code?: string | null;
      profiles: {
        nick_name: string;
        email: string;
        phone?: string;
      };
      user_addresses?: {
        id: string;
        full_name: string;
        phone_number: string;
        address_line1: string;
        address_line2?: string;
        postal_code: string;
      };
    };
    products: {
      product_id: string;
      product_name: string;
      seller_id: string;
    };
  };
}

export default function SellerRefundManagement() {
  const { t } = useTranslation();
  const isDarkMode = useDarkMode();
  const styles = getClozedStyles(isDarkMode);
  const { user } = useAuth();
  
  const [refundRequests, setRefundRequests] = useState<RefundRequest[]>([]);
  const [filteredRequests, setFilteredRequests] = useState<RefundRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // 필터 상태
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');

  // 페이지네이션 상태
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  
  // 상세 보기 다이얼로그
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState<RefundRequest | null>(null);
  
  // 액션 다이얼로그 (승인/거부)
  const [actionDialogOpen, setActionDialogOpen] = useState(false);
  const [actionType, setActionType] = useState<'approve' | 'reject'>('approve');
  const [adminNote, setAdminNote] = useState('');
  const [actionLoading, setActionLoading] = useState(false);

  useEffect(() => {
    if (user) {
      loadRefundRequests();
    }
  }, [user, page, rowsPerPage, statusFilter, typeFilter]);

  useEffect(() => {
    filterRequests();
  }, [refundRequests, searchTerm]);

  const loadRefundRequests = async () => {
    try {
      setLoading(true);
      setError(null);

      if (!user?.id) {
        throw new Error('사용자 정보를 찾을 수 없습니다.');
      }

      // 1. Seller 권한 확인 (Admin과 동일한 패턴)
      const { data: profile, error: profileError } = await supabase
        .from('profiles')
        .select('role')
        .eq('id', user.id)
        .single();

      if (profileError || !['seller', 'creator'].includes(profile?.role)) {
        console.error('User is not seller/creator or profile error:', profileError);
        throw new Error('판매자 권한이 없습니다.');
      }

      console.log('Seller access confirmed, loading refund requests...');
      
      // RLS 우회를 위해 service role key 사용 또는 다른 방법 필요
      // 임시로 빈 배열 대신 admin 방식으로 시도

      // 2. seller_profile에서 seller 정보 가져오기
      const { data: sellerProfile, error: sellerProfileError } = await supabase
        .from('seller_profile')
        .select('id')
        .eq('user_id', user.id)
        .single();

      if (sellerProfileError || !sellerProfile) {
        console.error('Seller Profile 정보를 찾을 수 없습니다:', sellerProfileError);
        throw new Error('판매자 프로필을 찾을 수 없습니다.');
      }

      console.log('Seller Profile ID:', sellerProfile.id);

      // 필터 조건 구성
      let query = supabase
        .from('cancel_exchange_requests')
        .select(`
          *,
          order_items (
            id,
            product_name,
            total_price,
            quantity,
            color_name,
            size,
            orders (
              id,
              shipping_address_id,
              guest_recipient_name,
              guest_address,
              guest_detail_address,
              guest_postal_code,
              guest_phone,
              profiles (
                nick_name,
                email,
                phone
              )
            ),
            products (
              product_id,
              product_name,
              seller_id
            )
          )
        `, { count: 'exact' });

      // 상태 필터 적용
      if (statusFilter !== 'all') {
        query = query.eq('status', statusFilter);
      }

      // 타입 필터 적용
      if (typeFilter !== 'all') {
        query = query.eq('type', typeFilter);
      }

      // 페이지네이션 적용
      const from = page * rowsPerPage;
      const to = from + rowsPerPage - 1;

      const { data: refundData, error: refundError, count } = await query
        .order('created_at', { ascending: false })
        .range(from, to);

      console.log('페이지네이션 환불 요청:', refundData);
      console.log('총 개수:', count);

      if (refundError) {
        console.error('환불 요청 조회 실패:', refundError);
        throw refundError;
      }

      // 총 개수 설정
      setTotalCount(count || 0);

      // 대체 방법: 전체 데이터 조회 후 필터링 (RLS 문제 해결용)
      const { data: allRefundData, error: allRefundError } = await supabase
        .from('cancel_exchange_requests')
        .select(`
          *,
          order_items (
            id,
            product_name,
            total_price,
            quantity,
            color_name,
            size,
            orders (
              id,
              shipping_address_id,
              guest_recipient_name,
              guest_address,
              guest_detail_address,
              guest_postal_code,
              guest_phone,
              profiles (
                nick_name,
                email,
                phone
              )
            ),
            products (
              product_id,
              product_name,
              seller_id
            )
          )
        `)
        .order('created_at', { ascending: false });

      console.log('모든 환불 요청 with relations:', refundData);
      console.log('환불 요청 조회 오류:', refundError);

      if (refundError) {
        console.error('환불 요청 조회 실패:', refundError);
        throw refundError;
      }

      // 판매자 상품만 필터링 (클라이언트 사이드에서)
      const sellerRefunds = refundData?.filter(refund =>
        refund.order_items?.products?.seller_id === sellerProfile.id
      ) || [];

      // 각 환불 요청에 대해 배송지 정보 추가로 조회
      const refundsWithAddresses = await Promise.all(
        sellerRefunds.map(async (refund) => {
          if (refund.order_items?.orders?.shipping_address_id) {
            const { data: addressData, error: addressError } = await supabase
              .from('user_addresses')
              .select('id, full_name, phone_number, address_line1, address_line2, postal_code')
              .eq('id', refund.order_items.orders.shipping_address_id)
              .single();

            if (!addressError && addressData) {
              return {
                ...refund,
                order_items: {
                  ...refund.order_items,
                  orders: {
                    ...refund.order_items.orders,
                    user_addresses: addressData
                  }
                }
              };
            }
          }
          return refund;
        })
      );

      console.log('필터링된 판매자 환불 요청 (주소 포함):', refundsWithAddresses);
      setRefundRequests(refundsWithAddresses);

    } catch (error) {
      console.error('Refund requests loading error:', error);
      setError(error instanceof Error ? error.message : '데이터를 불러오는 중 오류가 발생했습니다.');
    } finally {
      setLoading(false);
    }
  };

  const filterRequests = () => {
    let filtered = refundRequests;

    // 검색어 필터
    if (searchTerm) {
      filtered = filtered.filter(request => {
        const productName = request.order_items?.product_name || '';
        const customerName = request.order_items?.orders?.profiles?.nick_name || '';
        const orderId = request.order_items?.orders?.id || '';
        
        return productName.toLowerCase().includes(searchTerm.toLowerCase()) ||
               customerName.toLowerCase().includes(searchTerm.toLowerCase()) ||
               orderId.toLowerCase().includes(searchTerm.toLowerCase());
      });
    }

    // 상태 필터
    if (statusFilter !== 'all') {
      filtered = filtered.filter(request => request.status === statusFilter);
    }

    // 타입 필터
    if (typeFilter !== 'all') {
      filtered = filtered.filter(request => request.type === typeFilter);
    }

    setFilteredRequests(filtered);
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pending': return 'warning';
      case 'approved': return 'info';
      case 'rejected': return 'error';
      case 'processing': return 'primary';
      case 'completed': return 'success';
      default: return 'default';
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'pending': return 'Pending';
      case 'approved': return 'Approved';
      case 'rejected': return 'Rejected';
      case 'processing': return 'Processing';
      case 'completed': return 'Completed';
      default: return status;
    }
  };

  const getTypeColor = (type: string) => {
    switch (type) {
      case 'cancel': return 'error';
      case 'exchange': return 'warning';
      case 'return': return 'info';
      default: return 'default';
    }
  };

  const getTypeText = (type: string) => {
    switch (type) {
      case 'cancel': return 'Cancel';
      case 'exchange': return 'Exchange';
      case 'return': return 'Return';
      default: return type;
    }
  };

  const getTypeIcon = (type: string) => {
    switch (type) {
      case 'cancel': return <CancelPresentation />;
      case 'exchange': return <SwapHoriz />;
      case 'return': return <Undo />;
      default: return <Assignment />;
    }
  };

  const handleViewDetail = (request: RefundRequest) => {
    setSelectedRequest(request);
    setDetailDialogOpen(true);
  };

  const handleAction = (request: RefundRequest, action: 'approve' | 'reject') => {
    setSelectedRequest(request);
    setActionType(action);
    setAdminNote('');
    setActionDialogOpen(true);
  };

  const handleActionConfirm = async () => {
    if (!selectedRequest) return;

    try {
      setActionLoading(true);

      const newStatus = actionType === 'approve' ? 'approved' : 'rejected';

      const { error } = await supabase
        .from('cancel_exchange_requests')
        .update({
          status: newStatus,
          admin_note: adminNote || `판매자가 ${actionType === 'approve' ? '승인' : '거부'}함`,
          processed_at: new Date().toISOString()
        })
        .eq('id', selectedRequest.id);

      if (error) throw error;

      // 취소(cancel)는 앱에서 즉시 처리되므로 판매자 승인 불필요 (주석 처리)
      // 환불(refund)과 교환(exchange)만 판매자 승인 필요

      // 승인된 경우 결제 취소 처리 (환불만 처리, 취소/교환 제외)
      if (actionType === 'approve' && selectedRequest.type === 'refund') {
        console.log('🔍 [SELLER DEBUG] 환불 승인 - 부분 결제 취소 시작');

        try {
          // order_item_id로 order_id와 상품 금액 정보 조회
          const { data: orderItem, error: orderItemError } = await supabase
            .from('order_items')
            .select('order_id, product_price, quantity')
            .eq('id', selectedRequest.order_item_id)
            .single();

          if (orderItemError || !orderItem) {
            console.error('❌ [SELLER DEBUG] 주문 정보 조회 실패:', orderItemError);
            alert(t('sellerRefund.noOrderInfo'));
            return;
          }

          // 개별 상품의 총 금액 계산
          const itemTotalAmount = orderItem.product_price * orderItem.quantity;
          console.log('🔍 [SELLER DEBUG] 찾은 order_id:', orderItem.order_id);
          console.log('🔍 [SELLER DEBUG] 부분 환불 금액:', itemTotalAmount);

          // 부분 취소 API 호출
          const { data, error: cancelError } = await supabase.functions.invoke('cancel-payment', {
            body: {
              order_id: orderItem.order_id,
              order_item_id: selectedRequest.order_item_id,
              refund_amount: itemTotalAmount, // 개별 상품 금액만 환불
              cancel_reason: `환불 승인 - ${adminNote || selectedRequest.reason}`,
              admin_note: adminNote || '판매자가 환불 승인함'
            }
          });

          if (cancelError) {
            console.error('❌ [SELLER DEBUG] 결제 취소 실패:', cancelError);
            alert(t('sellerRefund.paymentCancelFailed', { message: cancelError.message }));
            return;
          }

          console.log('✅ [SELLER DEBUG] 부분 결제 취소 성공:', data);
        } catch (error) {
          console.error('❌ [SELLER DEBUG] 결제 취소 오류:', error);
          alert(t('sellerRefund.paymentCancelError'));
          return;
        }
      }

      // 승인된 경우 order_items 테이블의 배송 상태도 업데이트
      if (actionType === 'approve') {
        console.log('🔍 [DEBUG] 승인 처리 시작');
        console.log('🔍 [DEBUG] selectedRequest.type:', selectedRequest.type);

        // 취소는 앱에서 이미 처리되므로 환불과 교환만 처리
        let orderItemStatus = '';
        if (selectedRequest.type === 'refund') {
          orderItemStatus = 'refunded'; // 환불 -> 환불됨
        } else if (selectedRequest.type === 'exchange') {
          orderItemStatus = 'confirmed'; // 교환 -> 상태 유지
        } else if (selectedRequest.type === 'cancel') {
          // 취소는 DB 트리거에서 자동 처리되므로 스킵
          console.log('⚠️ [DEBUG] 취소 요청은 DB 트리거에서 자동 처리됨 - 상태 업데이트 스킵');
        } else {
          console.log('⚠️ [DEBUG] 알 수 없는 요청 타입:', selectedRequest.type);
        }

        if (orderItemStatus) {
          console.log('🔍 [DEBUG] 계산된 orderItemStatus:', orderItemStatus);

          // 디버깅: 현재 세션 확인
          const { data: sessionData } = await supabase.auth.getSession();
          console.log('🔍 [DEBUG] Current session:', sessionData.session ? 'Active' : 'None');
          console.log('🔍 [DEBUG] User ID:', sessionData.session?.user?.id);

          const { error: orderItemError, data: updateData } = await supabase
            .from('order_items')
            .update({
              item_delivery_status: orderItemStatus,
              updated_at: new Date().toISOString()
            })
            .eq('id', selectedRequest.order_item_id)
            .select();

          if (orderItemError) {
            console.error('❌ [DEBUG] Error updating order item status:', orderItemError);
            console.error('❌ [DEBUG] Error code:', orderItemError.code);
            console.error('❌ [DEBUG] Error message:', orderItemError.message);
            console.error('❌ [DEBUG] Error details:', orderItemError.details);
            console.error('❌ [DEBUG] Error hint:', orderItemError.hint);
            alert(t('sellerRefund.statusUpdateFailed'));
          } else {
            console.log(`✅ [DEBUG] Order item ${selectedRequest.order_item_id} status updated to ${orderItemStatus}`);
            console.log('✅ [DEBUG] Updated data:', updateData);
          }
        }
      }

      // 데이터 새로고침
      await loadRefundRequests();

      setActionDialogOpen(false);
      setSelectedRequest(null);
      setAdminNote('');

      // 교환 승인 시 재고 수정 안내 추가
      if (actionType === 'approve' && selectedRequest.type === 'exchange') {
        alert(t('sellerRefund.exchangeApproveSuccess'));
      } else {
        alert(`${actionType === 'approve' ? t('sellerRefund.approveSuccess') : t('sellerRefund.rejectSuccess')}`);
      }

    } catch (error) {
      console.error('액션 처리 오류:', error);
      alert(t('sellerRefund.processErrorMsg'));
    } finally {
      setActionLoading(false);
    }
  };

  const formatCurrency = (amount: number) => {
    return `₩${amount.toLocaleString()}`;
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('ko-KR', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // 통계 계산
  const totalRequests = refundRequests.length;
  const pendingRequests = refundRequests.filter(r => r.status === 'pending').length;
  const completedRequests = refundRequests.filter(r => r.status === 'completed').length;
  const totalRefundAmount = refundRequests
    .filter(r => r.status === 'completed')
    .reduce((sum, r) => sum + (r.order_items?.total_price || 0), 0);

  if (loading) {
    return (
      <Box sx={styles.pageContainer}>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <CircularProgress />
        </Box>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={styles.pageContainer}>
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={styles.pageContainer}>
      <Typography variant="h4" fontWeight={700} mb={3} sx={styles.title}>
        환불 관리
      </Typography>

      {/* 요약 카드 */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} md={3}>
          <Card sx={styles.paper}>
            <CardContent sx={{ p: 3 }}>
              <Stack direction="row" alignItems="center" spacing={2}>
                <Avatar sx={{ bgcolor: 'primary.main' }}>
                  <Assignment />
                </Avatar>
                <Box>
                  <Typography variant="body2" sx={styles.text}>
                    총 요청
                  </Typography>
                  <Typography variant="h5" fontWeight={700} sx={styles.text}>
                    {totalRequests}건
                  </Typography>
                </Box>
              </Stack>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={3}>
          <Card sx={styles.paper}>
            <CardContent sx={{ p: 3 }}>
              <Stack direction="row" alignItems="center" spacing={2}>
                <Avatar sx={{ bgcolor: 'warning.main' }}>
                  <Schedule />
                </Avatar>
                <Box>
                  <Typography variant="body2" sx={styles.text}>
                    대기중
                  </Typography>
                  <Typography variant="h5" fontWeight={700} sx={styles.text}>
                    {pendingRequests}건
                  </Typography>
                </Box>
              </Stack>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={3}>
          <Card sx={styles.paper}>
            <CardContent sx={{ p: 3 }}>
              <Stack direction="row" alignItems="center" spacing={2}>
                <Avatar sx={{ bgcolor: 'success.main' }}>
                  <CheckCircle />
                </Avatar>
                <Box>
                  <Typography variant="body2" sx={styles.text}>
                    완료
                  </Typography>
                  <Typography variant="h5" fontWeight={700} sx={styles.text}>
                    {completedRequests}건
                  </Typography>
                </Box>
              </Stack>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={3}>
          <Card sx={styles.paper}>
            <CardContent sx={{ p: 3 }}>
              <Stack direction="row" alignItems="center" spacing={2}>
                <Avatar sx={{ bgcolor: 'error.main' }}>
                  <AttachMoney />
                </Avatar>
                <Box>
                  <Typography variant="body2" sx={styles.text}>
                    환불 금액
                  </Typography>
                  <Typography variant="h5" fontWeight={700} sx={styles.text}>
                    {formatCurrency(totalRefundAmount)}
                  </Typography>
                </Box>
              </Stack>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* 필터 및 검색 */}
      <Paper sx={{ ...styles.paper, p: 3, mb: 3 }}>
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              placeholder={t("sellerRefund.searchPlaceholder")}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              sx={styles.input}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <FormControl fullWidth sx={styles.input}>
              <InputLabel sx={styles.text}>{t('sellerRefund.statusLabel')}</InputLabel>
              <Select
                value={statusFilter}
                label={t('sellerRefund.statusLabel')}
                onChange={(e) => setStatusFilter(e.target.value)}
                sx={styles.input}
                MenuProps={{
                  PaperProps: {
                    sx: styles.selectMenu
                  }
                }}
              >
                <MenuItem value="all" sx={styles.menuItem}>{t('sellerRefund.allStatus')}</MenuItem>
                <MenuItem value="pending" sx={styles.menuItem}>{t('sellerRefund.pendingStatus')}</MenuItem>
                <MenuItem value="approved" sx={styles.menuItem}>{t('sellerRefund.approvedStatus')}</MenuItem>
                <MenuItem value="rejected" sx={styles.menuItem}>{t('sellerRefund.rejectedStatus')}</MenuItem>
                <MenuItem value="processing" sx={styles.menuItem}>{t('sellerRefund.processingStatus')}</MenuItem>
                <MenuItem value="completed" sx={styles.menuItem}>{t('sellerRefund.completedStatus')}</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={4}>
            <FormControl fullWidth sx={styles.input}>
              <InputLabel sx={styles.text}>{t('sellerRefund.typeLabel')}</InputLabel>
              <Select
                value={typeFilter}
                label={t('sellerRefund.typeLabel')}
                onChange={(e) => setTypeFilter(e.target.value)}
                sx={styles.input}
                MenuProps={{
                  PaperProps: {
                    sx: styles.selectMenu
                  }
                }}
              >
                <MenuItem value="all" sx={styles.menuItem}>{t('sellerRefund.allStatus')}</MenuItem>
                <MenuItem value="cancel" sx={styles.menuItem}>{t("common.cancel")}</MenuItem>
                <MenuItem value="exchange" sx={styles.menuItem}>{t('sellerRefund.exchangeType')}</MenuItem>
                <MenuItem value="refund" sx={styles.menuItem}>{t('sellerRefund.refundType')}</MenuItem>
              </Select>
            </FormControl>
          </Grid>
        </Grid>

        {/* 환불 요청 테이블 */}
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell sx={styles.text}>{t('sellerRefund.requestId')}</TableCell>
                <TableCell sx={styles.text}>{t('sellerRefund.typeCol')}</TableCell>
                <TableCell sx={styles.text}>{t('sellerRefund.productInfo')}</TableCell>
                <TableCell sx={styles.text}>{t('sellerRefund.customerInfo')}</TableCell>
                <TableCell align="right" sx={styles.text}>{t('sellerRefund.amount')}</TableCell>
                <TableCell sx={styles.text}>{t('sellerRefund.reasonCol')}</TableCell>
                <TableCell sx={styles.text}>{t('sellerRefund.stateCol')}</TableCell>
                <TableCell sx={styles.text}>{t('sellerRefund.requestDate')}</TableCell>
                <TableCell align="center" sx={styles.text}>{t('sellerRefund.actions')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredRequests.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={9} align="center">
                    <Typography sx={styles.text}>
                      환불 요청이 없습니다.
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                filteredRequests.map((request) => (
                  <TableRow key={request.id} hover>
                    <TableCell >
                      <Typography variant="body2" fontFamily="monospace" sx={styles.text}>
                        {request.id.slice(-8)}
                      </Typography>
                    </TableCell>
                    <TableCell >
                      <Chip
                        icon={getTypeIcon(request.type)}
                        label={getTypeText(request.type)}
                        color={getTypeColor(request.type) as any}
                        size="small"
                      />
                    </TableCell>
                    <TableCell >
                      <Box>
                        <Typography variant="body2" fontWeight="bold" sx={styles.text}>
                          {request.order_items?.product_name}
                        </Typography>
                        <Typography variant="caption" sx={styles.text}>
                          수량: {request.order_items?.quantity}
                          {request.order_items?.color_name && ` | 색상: ${request.order_items.color_name}`}
                          {request.order_items?.size && ` | 사이즈: ${request.order_items.size}`}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell >
                      <Box>
                        <Typography variant="body2" sx={styles.text}>
                          {request.order_items?.orders?.profiles?.nick_name || '알 수 없음'}
                        </Typography>
                        <Typography variant="caption" sx={styles.text}>
                          {request.order_items?.orders?.profiles?.email}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell align="right" >
                      <Typography variant="body2" fontWeight="bold" sx={styles.text}>
                        {formatCurrency(request.order_items?.total_price || 0)}
                      </Typography>
                    </TableCell>
                    <TableCell >
                      <Typography variant="body2" sx={{ ...styles.text, maxWidth: 200 }}>
                        {request.reason}
                      </Typography>
                    </TableCell>
                    <TableCell >
                      <Chip
                        label={getStatusText(request.status)}
                        color={getStatusColor(request.status) as any}
                        size="small"
                      />
                    </TableCell>
                    <TableCell >
                      <Typography variant="body2" sx={styles.text}>
                        {formatDate(request.created_at)}
                      </Typography>
                    </TableCell>
                    <TableCell align="center" >
                      <Stack direction="row" spacing={1}>
                        <IconButton 
                          size="small" 
                          onClick={() => handleViewDetail(request)}
                                                  >
                          <Visibility />
                        </IconButton>
                        {request.status === 'pending' && (
                          <>
                            <IconButton
                              size="small"
                              color="success"
                              onClick={() => handleAction(request, 'approve')}
                              title={t('sellerRefund.approve')}
                                                          >
                              <CheckCircle />
                            </IconButton>
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => handleAction(request, 'reject')}
                              title={t('sellerRefund.reject')}
                                                          >
                              <Cancel />
                            </IconButton>
                          </>
                        )}
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
          <TablePagination
            component="div"
            count={totalCount}
            page={page}
            onPageChange={handleChangePage}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            rowsPerPageOptions={[5, 10, 25, 50]}
            sx={styles.text}
            labelRowsPerPage={t('sellerRefund.rowsPerPage')}
            labelDisplayedRows={({ from, to, count }) => t('sellerRefund.displayedRows', { from, to, count: (count !== -1 ? count : `${to}${t('sellerRefund.noMore')}`) as unknown as number })}
          />
        </TableContainer>
      </Paper>

      {/* 상세 보기 다이얼로그 */}
      <Dialog
        open={detailDialogOpen}
        onClose={() => setDetailDialogOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{ sx: styles.paper }}
      >
        <DialogTitle sx={styles.text}>
          환불 요청 상세 - {selectedRequest && getTypeText(selectedRequest.type)}
        </DialogTitle>
        <DialogContent>
          {selectedRequest && (
            <Box>
              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={6}>
                  <Typography variant="subtitle2" sx={styles.text}>{t('sellerRefund.requestIdLabel')}</Typography>
                  <Typography variant="body1" sx={styles.text}>{selectedRequest.id}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="subtitle2" sx={styles.text}>{t('sellerRefund.requestType')}</Typography>
                  <Chip
                    icon={getTypeIcon(selectedRequest.type)}
                    label={getTypeText(selectedRequest.type)}
                    color={getTypeColor(selectedRequest.type) as any}
                    size="small"
                  />
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="subtitle2" sx={styles.text}>{t('sellerRefund.productName')}</Typography>
                  <Typography variant="body1" sx={styles.text}>{selectedRequest.order_items?.product_name}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="subtitle2" sx={styles.text}>{t('sellerRefund.orderAmount')}</Typography>
                  <Typography variant="body1" sx={styles.text}>{formatCurrency(selectedRequest.order_items?.total_price || 0)}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="subtitle2" sx={styles.text}>{t('sellerRefund.customerName')}</Typography>
                  <Typography variant="body1" sx={styles.text}>{selectedRequest.order_items?.orders?.profiles?.nick_name}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="subtitle2" sx={styles.text}>{t('sellerRefund.customerEmail')}</Typography>
                  <Typography variant="body1" sx={styles.text}>{selectedRequest.order_items?.orders?.profiles?.email}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="subtitle2" sx={styles.text}>{t('sellerRefund.customerPhone')}</Typography>
                  <Typography variant="body1" sx={styles.text}>
                    {selectedRequest.order_items?.orders?.profiles?.phone || '정보 없음'}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="subtitle2" sx={styles.text}>{t('sellerRefund.stateCol')}</Typography>
                  <Chip
                    label={getStatusText(selectedRequest.status)}
                    color={getStatusColor(selectedRequest.status) as any}
                    size="small"
                  />
                </Grid>

                {/* 배송지 정보 */}
                {(() => {
                  const addr = selectedRequest.order_items?.orders?.user_addresses;
                  const order = selectedRequest.order_items?.orders;
                  const recipientName = addr?.full_name || order?.guest_recipient_name;
                  const recipientPhone = addr?.phone_number || order?.guest_phone;
                  const address1 = addr?.address_line1 || order?.guest_address;
                  const address2 = addr?.address_line2 || order?.guest_detail_address;
                  const postalCode = addr?.postal_code || order?.guest_postal_code;
                  if (!recipientName && !address1) return null;
                  return (
                    <>
                      <Grid item xs={12}>
                        <Divider sx={{ my: 1 }} />
                        <Typography variant="h6" sx={{ ...styles.text, mt: 1, mb: 1 }}>{t('sellerRefund.shippingAddress')}</Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography variant="subtitle2" sx={styles.text}>{t('sellerRefund.recipient')}</Typography>
                        <Typography variant="body1" sx={styles.text}>{recipientName}</Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography variant="subtitle2" sx={styles.text}>{t('sellerRefund.contactInfo')}</Typography>
                        <Typography variant="body1" sx={styles.text}>{recipientPhone}</Typography>
                      </Grid>
                      <Grid item xs={12}>
                        <Typography variant="subtitle2" sx={styles.text}>{t('sellerRefund.address')}</Typography>
                        <Typography variant="body1" sx={styles.text}>
                          {address1}{address2 && `, ${address2}`}
                        </Typography>
                        {postalCode && (
                          <Typography variant="body2" color="text.secondary" sx={styles.text}>
                            우편번호: {postalCode}
                          </Typography>
                        )}
                      </Grid>
                      <Grid item xs={12}>
                        <Divider sx={{ my: 1 }} />
                      </Grid>
                    </>
                  );
                })()}
                <Grid item xs={12}>
                  <Typography variant="subtitle2" sx={styles.text}>{t('sellerRefund.refundReason')}</Typography>
                  <Typography variant="body1" sx={styles.text}>{selectedRequest.reason}</Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="subtitle2" sx={styles.text}>{t('sellerRefund.detailContent')}</Typography>
                  <Typography variant="body1" sx={styles.text}>{selectedRequest.detail || t('sellerRefund.noDetail')}</Typography>
                </Grid>

                {/* 첨부 이미지 */}
                {selectedRequest.image_url && selectedRequest.image_url.length > 0 && (
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" sx={styles.text}>{t('sellerRefund.attachedImages')}</Typography>
                    <Box sx={{ mt: 1, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                      {(Array.isArray(selectedRequest.image_url)
                        ? selectedRequest.image_url
                        : [selectedRequest.image_url]
                      ).map((url, index) => (
                        <Box
                          key={index}
                          component="img"
                          src={url}
                          alt={`첨부 이미지 ${index + 1}`}
                          sx={{
                            width: 200,
                            height: 200,
                            objectFit: 'cover',
                            borderRadius: 1,
                            border: '1px solid #ddd',
                            cursor: 'pointer',
                          }}
                          onClick={() => window.open(url, '_blank')}
                        />
                      ))}
                    </Box>
                  </Grid>
                )}

                {selectedRequest.admin_note && (
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" sx={styles.text}>{t('sellerRefund.adminNote')}</Typography>
                    <Typography variant="body1" sx={styles.text}>{selectedRequest.admin_note}</Typography>
                  </Grid>
                )}
                <Grid item xs={6}>
                  <Typography variant="subtitle2" sx={styles.text}>{t('sellerRefund.requestDateLabel')}</Typography>
                  <Typography variant="body1" sx={styles.text}>{formatDate(selectedRequest.created_at)}</Typography>
                </Grid>
                {selectedRequest.processed_at && (
                  <Grid item xs={6}>
                    <Typography variant="subtitle2" sx={styles.text}>{t('sellerRefund.processDate')}</Typography>
                    <Typography variant="body1" sx={styles.text}>{formatDate(selectedRequest.processed_at)}</Typography>
                  </Grid>
                )}
              </Grid>
              
              <Alert severity="info" sx={{ mt: 2 }}>
                <strong>{t('sellerRefund.approveAutoProcess')}:</strong><br/>
                • {t('sellerRefund.approveRefundDesc')}<br/>
                • {t('sellerRefund.approveExchangeDesc')}<br/>
                • 거부 시: 요청이 반려되며 고객에게 알림
              </Alert>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetailDialogOpen(false)} sx={styles.text}>{t("common.close")}</Button>
        </DialogActions>
      </Dialog>

      {/* 액션 확인 다이얼로그 */}
      <Dialog
        open={actionDialogOpen}
        onClose={() => !actionLoading && setActionDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: styles.paper }}
      >
        <DialogTitle sx={styles.text}>
          {actionType === 'approve' ? '환불 요청 승인' : '환불 요청 거부'}
        </DialogTitle>
        <DialogContent>
          <Alert severity={actionType === 'approve' ? 'warning' : 'error'} sx={{ mb: 2 }}>
            {actionType === 'approve'
              ? selectedRequest?.type === 'refund'
                ? '⚠️ 환불 승인 시 즉시 결제가 부분 취소되고 환불 처리됩니다. 승인하시겠습니까?'
                : selectedRequest?.type === 'exchange'
                ? '⚠️ 교환 승인 시 상품을 재발송해야 합니다. 승인하시겠습니까?'
                : '이 요청을 승인하시겠습니까?'
              : '⚠️ 거부 시 되돌릴 수 없습니다. 정말 거부하시겠습니까?'}
          </Alert>
          {selectedRequest && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" sx={styles.text}>{t('sellerRefund.requestInfo')}</Typography>
              <Typography variant="body2" sx={styles.text}>{t('sellerRefund.typeInfo', { type: getTypeText(selectedRequest.type) })}</Typography>
              <Typography variant="body2" sx={styles.text}>{t('sellerRefund.productLabel', { name: selectedRequest.order_items?.product_name })}</Typography>
              <Typography variant="body2" sx={styles.text}>{t('sellerRefund.amountLabel', { amount: formatCurrency(selectedRequest.order_items?.total_price || 0) })}</Typography>
            </Box>
          )}
          <TextField
            fullWidth
            multiline
            rows={3}
            label={t('sellerRefund.processingNote')}
            value={adminNote}
            onChange={(e) => setAdminNote(e.target.value)}
            placeholder={t('sellerRefund.processingNotePlaceholder')}
            sx={styles.input}
            disabled={actionLoading}
          />
          {actionType === 'reject' && (
            <Alert severity="info" sx={{ mt: 2 }}>
              거절 사유가 고객에게 표기됩니다
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setActionDialogOpen(false)}
            sx={styles.text}
            disabled={actionLoading}
          >
            취소
          </Button>
          <Button
            onClick={handleActionConfirm}
            variant="contained"
            color={actionType === 'approve' ? 'primary' : 'error'}
            disabled={actionLoading}
            startIcon={actionLoading ? <CircularProgress size={20} color="inherit" /> : null}
          >
            {actionLoading
              ? '처리 중...'
              : actionType === 'approve' ? '승인' : '거부'
            }
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}